import React, { useState, useMemo } from 'react';
import { Badge } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { API } from '../../config/api';
// One answer to how a machine's queue joins up, shared with the queue list and
// the queue editor — see helpers/machineQueueBlocks.js.
import { buildBlockRows } from '../../helpers/machineQueueBlocks.js';
// Who a drum is for — CLIENT / STOCK / MIXED plus the customer's name. Same
// helper the machine-queue table and Project Token State read, so the three
// cannot disagree about whose cable this is.
import { buildClientIndex, deriveDestination } from '../../helpers/orderDestination';
import { useOrders } from '../../features/orders/useOrders';

const http = axios.create({ baseURL: API.REDIS, timeout: 10000 });

// :5100 requires a token; `axios.create()` snapshots the global defaults, so one
// written later by `setAuthToken` never reaches this instance.
http.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['x-auth-token'] = token;
  }
  return config;
});

// ─── MACHINE DISPLAY NAMES ────────────────────────────────
const NAMES = {
  l75: 'L75 + L45', l120: 'L120 Tandem', l70: 'L70',
  dtb1250: 'DTB 1250', skip2250: 'Skip 2250', ach300: 'ACH 300',
  dhc600: 'DHC 600', ath600: 'ATH 600'
};

const COLORS = {
  l75: '#3874ff', l70: '#0fc6c2', l120: '#e5780b',
  dtb1250: '#8b5cf6', skip2250: '#f43f5e', ach300: '#10b981',
  dhc600: '#f59e0b', ath600: '#6366f1'
};

// ─── STATUS HELPERS ────────────────────────────────────────
const statusBg = s => s === 'running' ? 'success' : s === 'stopped' ? 'danger' : 'warning';
const fmt = (v, d = 1) => Number(v || 0).toFixed(d);

// ─── MAIN PANEL ────────────────────────────────────────────
export default function MachinePanel({ machineId, data, onClose }) {
  const [tab, setTab] = useState('hmi');
  if (!machineId) return null;

  const name = NAMES[machineId] || machineId.toUpperCase();
  const color = COLORS[machineId] || '#888';

  return (
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        background: '#1a1f2e', color: '#FFF', borderRadius: 12,
        width: '90%', maxWidth: 700, maxHeight: '85vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        border: `2px solid ${color}`, boxShadow: `0 0 40px ${color}30`
      }}>
        {/* Header */}
        <div style={{
          padding: '12px 16px', borderBottom: '1px solid #333',
          display: 'flex', alignItems: 'center', gap: 10,
          background: '#12151f'
        }}>
          <div style={{ width: 4, height: 28, borderRadius: 2, background: color }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{name}</div>
            <div style={{ fontSize: 11, opacity: 0.5 }}>Factory 3D — Machine Panel</div>
          </div>
          {data && (
            <Badge bg={statusBg(data.status)} className="text-uppercase" style={{ fontSize: 11 }}>
              {data.status}
            </Badge>
          )}
          <div onClick={onClose} style={{ cursor: 'pointer', padding: 4 }}>
            <FeatherIcon icon="x" size={20} />
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #333', background: '#12151f' }}>
          {['hmi', 'jobs'].map(t => (
            <div
              key={t}
              onClick={() => setTab(t)}
              style={{
                padding: '8px 20px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: 1,
                borderBottom: tab === t ? `2px solid ${color}` : '2px solid transparent',
                color: tab === t ? color : '#888'
              }}
            >
              {t === 'hmi' ? 'Machine Data' : 'Jobs Queue'}
            </div>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {tab === 'hmi' ? (
            <HMIContent machineId={machineId} data={data} />
          ) : (
            <JobsContent machineId={machineId} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── HMI CONTENT ───────────────────────────────────────────

function HMIContent({ machineId, data }) {
  if (!data) return <div style={{ textAlign: 'center', opacity: 0.5 }}>No data available</div>;

  const plc = data.plc || {};
  const isRpi = ['l70', 'dhc600', 'ath600'].includes(machineId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Main metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        <MetricBox label="Speed" value={`${fmt(data.speed)} m/min`} color={data.speed > 0 ? '#4ADE80' : '#888'} />
        <MetricBox label="Meter Act" value={`${fmt(data.meterAct, 0)} m`} />
        <MetricBox label="Total Produced" value={`${fmt(data.meterTotal / 1000, 2)} km`} />
      </div>

      {data.mode && data.mode !== 'UNKNOWN' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          <MetricBox label="Mode" value={data.mode} color={data.mode === 'PRODUCTION' ? '#4ADE80' : '#F59E0B'} />
          <MetricBox label="Operator" value={data.operator || '—'} />
          <MetricBox label="Status" value={data.status?.toUpperCase()} color={data.isRunning ? '#4ADE80' : '#F59E0B'} />
        </div>
      )}

      {/* Machine-specific data */}
      {machineId === 'l75' && <L75Details plc={plc} data={data} />}
      {machineId === 'l120' && <L120Details plc={plc} />}
      {machineId === 'dtb1250' && <DTBDetails plc={plc} data={data} />}
      {machineId === 'skip2250' && <SkipDetails plc={plc} data={data} />}
      {machineId === 'ach300' && <ACHDetails plc={plc} />}
      {isRpi && <RPiDetails data={data} />}
    </div>
  );
}

function MetricBox({ label, value, color = '#FFF' }) {
  return (
    <div style={{
      background: '#0D1117', border: '1px solid #2A2F3A', borderRadius: 6,
      padding: '8px 10px', textAlign: 'center'
    }}>
      <div style={{ fontSize: 9, opacity: 0.5, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color, fontFamily: 'monospace' }}>{value}</div>
    </div>
  );
}

function L75Details({ plc, data }) {
  return (
    <div>
      <SectionTitle text="Components Status" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        <StatusDot label="Capstan" active={data.capstanOn} />
        <StatusDot label="E75 Extruder" active={data.e75On} />
        <StatusDot label="E45 Extruder" active={data.e45On} />
        <StatusDot label="Take-up 1" active={data.takeup1Active} />
        <StatusDot label="Take-up 2" active={data.takeup2Active} />
      </div>
      <SectionTitle text="Line Data" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        <MetricBox label="Winding RPM" value={fmt(plc.WINDING_ACT_RPM)} />
        <MetricBox label="Line Speed" value={`${fmt(plc.ACT_LINE_SPEED)} m/min`} />
        <MetricBox label="Meter Total" value={`${fmt(plc.METER_TOTAL, 0)} m`} />
      </div>
    </div>
  );
}

function L120Details({ plc }) {
  return (
    <div>
      <SectionTitle text="Extruder" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        <MetricBox label="Screw RPM" value={fmt(plc.SCREW_ACT_RPM)} />
        <MetricBox label="Current" value={`${fmt(plc.CURRENT_ACT)} A`} />
        <MetricBox label="Ramp" value={`${fmt(plc.ACT_RAMP_VALUE)} %`} />
      </div>
      <SectionTitle text="Components" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        <StatusDot label="Caterpillar" active={!!plc.CAT_ON_OFF} />
        <StatusDot label="Extruder" active={!!plc.E_120_J_ON_OFF} />
        <StatusDot label="Line Start" active={!!plc.LINE_START_RELAY} />
      </div>
    </div>
  );
}

function DTBDetails({ plc, data }) {
  return (
    <div>
      <SectionTitle text="Bow & Traverse" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        <MetricBox label="Bow Twist" value={`${fmt(plc.BOW_ACT_TWIST)} rot/min`} />
        <MetricBox label="Torque" value={`${fmt(plc.TORQE_ACT_PERCENTEC)} %`} />
        <MetricBox label="Bow Current" value={`${fmt(plc.BOW_CURRENT)} A`} />
      </div>
      <SectionTitle text="Pay-off Status" />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {(data.payoffs || []).map(po => (
          <div key={po.num} style={{
            width: 36, height: 36, borderRadius: 4, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700, color: '#FFF',
            background: po.emergency ? '#EF4444' : po.active ? '#22C55E' : '#333',
            border: po.active ? '2px solid #4ADE80' : '1px solid #555'
          }}>
            {po.num}
          </div>
        ))}
      </div>
    </div>
  );
}

function SkipDetails({ plc, data }) {
  return (
    <div>
      <SectionTitle text="Skip Data" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        <MetricBox label="TTH RPM" value={fmt(data.tthRpm)} />
        <MetricBox label="Capstan RPM" value={fmt(data.capstanRpm)} />
        <MetricBox label="Act Speed" value={`${fmt(plc.ACT_SPEED)} m/min`} />
      </div>
    </div>
  );
}

function ACHDetails({ plc }) {
  return (
    <div>
      <SectionTitle text="Coiling Data" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        <MetricBox label="Speed Act" value={`${fmt(plc.ACT_LINE_SPEED)} m/min`} />
        <MetricBox label="Winding RPM" value={fmt(plc.WINDING_ACT_RPM)} />
        <MetricBox label="Traverse Pos" value={fmt(plc.TRAVER_ACT_POS)} />
      </div>
    </div>
  );
}

function RPiDetails({ data }) {
  return (
    <div>
      <SectionTitle text="Raspberry Pi Data" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
        <MetricBox label="Line Speed" value={`${fmt(data.speed)} m/min`} color={data.speed > 0 ? '#4ADE80' : '#888'} />
        <MetricBox label="Total Meter" value={`${fmt(data.meterTotal, 0)} m`} />
      </div>
      <div style={{ marginTop: 8, padding: 8, background: '#0D1117', borderRadius: 6, border: '1px solid #2A2F3A', fontSize: 10, opacity: 0.6 }}>
        Limited data — Raspberry Pi integration (speed + meter only)
      </div>
    </div>
  );
}

function StatusDot({ label, active }) {
  return (
    <div style={{
      background: '#0D1117', border: '1px solid #2A2F3A', borderRadius: 6,
      padding: '6px 8px', display: 'flex', alignItems: 'center', gap: 6, fontSize: 11
    }}>
      <div style={{
        width: 8, height: 8, borderRadius: '50%',
        background: active ? '#22C55E' : '#555',
        boxShadow: active ? '0 0 6px #22C55E' : 'none'
      }} />
      <span style={{ color: active ? '#FFF' : '#888' }}>{label}</span>
    </div>
  );
}

function SectionTitle({ text }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#888', marginTop: 12, marginBottom: 6 }}>
      {text}
    </div>
  );
}

// ─── JOBS CONTENT ──────────────────────────────────────────

function JobsContent({ machineId }) {
  const { data: queue, isLoading } = useQuery({
    queryKey: ['machine-queue-3d', machineId],
    queryFn: async () => {
      const res = await http.get(`/api/redis/queues/${machineId}`);
      return res.data;
    },
    refetchInterval: 5000,
    enabled: !!machineId
  });

  // Who each drum is FOR. The operator opens this panel before the wizard, and
  // "is this somebody's order or is it stock" changes what they do about a
  // problem — it was the one thing the panel never said. Cached by react-query,
  // so the extra read costs nothing per open.
  const { data: ordersData } = useOrders();
  const clientIndex = useMemo(
    () => buildClientIndex(ordersData || []),
    [ordersData]
  );

  if (isLoading) return <div style={{ textAlign: 'center', opacity: 0.5 }}>Loading jobs...</div>;
  if (!queue?.groups?.length) return <div style={{ textAlign: 'center', opacity: 0.5 }}>No jobs in queue</div>;

  // Joined up the way the queue editor and the HMI join it, so the panel shows
  // the machine's real order: adjacent entries writing to one token are a
  // merge-execution and draw as one block, and consecutive blocks of a group-by
  // launch are one run under a single heading. This used to list the API's
  // per-job groups verbatim, which cannot express either — so a group-by
  // wire-diameter launch read as unrelated executions sitting next to each
  // other. See helpers/machineQueueBlocks.js.
  const rows = buildBlockRows(queue?.groups || []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 11, opacity: 0.5 }}>
        Queue: {queue.queueSize || 0} items
      </div>

      {rows.map(row => {
        const items = row.block?.items || [];
        const merged = items.length > 1;

        return (
          <div key={row.id} style={{
            background: '#0D1117', border: '1px solid #2A2F3A',
            borderLeft: `4px solid ${row.borderColor}`,
            borderRadius: 8, overflow: 'hidden'
          }}>
            {row.showJob && (
              <div style={{ padding: '8px 12px', borderBottom: '1px solid #2A2F3A' }}>
                {row.isGroupByRun && row.groupBy && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    {row.groupBy.kind === 'color' ? (
                      <>
                        <span style={{
                          background: getColorHex(row.groupBy.value), width: 12, height: 12,
                          borderRadius: 2, display: 'inline-block', border: '1px solid #555'
                        }} />
                        <span style={{ fontWeight: 800, fontSize: 13, letterSpacing: 1 }}>
                          {row.groupBy.value}
                        </span>
                      </>
                    ) : (
                      <span style={{ fontWeight: 800, fontSize: 13, letterSpacing: 1 }}>
                        &Oslash;{row.groupBy.value}
                      </span>
                    )}
                    <span style={{ fontSize: 9, opacity: 0.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                      grouped run
                    </span>
                  </div>
                )}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                  {row.products.map(p => {
                    const dest = deriveDestination(
                      p.allocations,
                      p.plannedLength,
                      clientIndex
                    );
                    const tint =
                      dest.destination === 'CLIENT'
                        ? '#3B82F6'
                        : dest.destination === 'MIXED'
                          ? '#F59E0B'
                          : '#6B7280';
                    return (
                      <span key={p.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontWeight: 700, fontSize: 13 }}>{p.productLabel}</span>
                        {p.section != null && (
                          <span style={{ fontSize: 10, opacity: 0.5 }}>
                            {p.section}mm&sup2;
                          </span>
                        )}
                        <span
                          style={{
                            fontSize: 9, letterSpacing: 0.5, textTransform: 'uppercase',
                            padding: '1px 6px', borderRadius: 4,
                            border: `1px solid ${tint}`, color: tint
                          }}
                          title={
                            dest.destination === 'STOCK'
                              ? 'Nobody has claimed this yet — it goes to stock'
                              : `${Math.round(dest.allocatedToOrders).toLocaleString()} m promised${
                                  dest.stockMeters > 0
                                    ? `, ${Math.round(dest.stockMeters).toLocaleString()} m to stock`
                                    : ''
                                }`
                          }
                        >
                          {dest.destination === 'CLIENT' ? 'client' : dest.destination.toLowerCase()}
                        </span>
                        {dest.clients.length > 0 && (
                          <span style={{ fontSize: 10, opacity: 0.75 }}>
                            {dest.clients.join(', ')}
                          </span>
                        )}
                        {dest.dueEarliest && (
                          <span style={{ fontSize: 9, opacity: 0.5 }}>
                            due {String(dest.dueEarliest).slice(0, 10)}
                          </span>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {merged && (
              <div style={{
                padding: '4px 12px', fontSize: 9, letterSpacing: 1,
                textTransform: 'uppercase', color: '#F59E0B',
                borderBottom: '1px solid #1A1F2A'
              }}>
                merge execution &mdash; {items.length} products on one drum
              </div>
            )}

            {items.map((item, ti) => {
              const task = item.task || {};
              return (
                <div key={task.taskId || ti} style={{ padding: '6px 12px', borderBottom: '1px solid #1A1F2A' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: '#888' }}>{task.phase?.replace(/_/g, ' ')}</span>
                      {task.color && (
                        <span style={{
                          background: getColorHex(task.color), width: 10, height: 10,
                          borderRadius: 2, display: 'inline-block', border: '1px solid #555'
                        }} />
                      )}
                    </div>
                    <span style={{ fontFamily: 'monospace', fontSize: 10 }}>
                      {fmt(task.plannedLength, 0)}m
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                    {(task.outputs || []).map((tok, tki) => (
                      <div key={tki} style={{
                        background: tok.status === 'DONE' ? '#1A3A1A' : tok.status === 'ACTIVE' ? '#1A2A3A' : '#1A1A2A',
                        border: `1px solid ${tok.status === 'ACTIVE' ? '#3B82F6' : '#333'}`,
                        borderRadius: 4, padding: '3px 6px', fontSize: 9, fontFamily: 'monospace'
                      }}>
                        <div style={{ color: '#888' }}>{tok.tokenId?.slice(0, 10)}</div>
                        <div>
                          <span style={{ color: tok.status === 'DONE' ? '#22C55E' : '#3B82F6' }}>
                            {fmt(tok.producedLength, 0)}
                          </span>
                          <span style={{ color: '#555' }}>/{fmt(tok.plannedLength, 0)}m</span>
                        </div>
                        {tok.progress > 0 && (
                          <div style={{ height: 2, background: '#333', borderRadius: 1, marginTop: 2 }}>
                            <div style={{ height: 2, background: '#3B82F6', borderRadius: 1, width: `${tok.progress}%` }} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function getColorHex(color) {
  const map = {
    BLUE: '#3B82F6', BROWN: '#8B4513', 'GREEN-YELLOW': '#9ACD32',
    GREY: '#808080', BLACK: '#333', WHITE: '#EEE', RED: '#EF4444',
    YELLOW: '#F59E0B', GREEN: '#22C55E', ORANGE: '#F97316'
  };
  return map[color] || '#888';
}
